import { useDashboardStore, type BuildingRenderer } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';

/**
 * Two-option toggle to flip the building-overview renderer between the
 * shiny Pixi canvas (default) and the DOM mock kept around for
 * debugging.  Choice persists in localStorage so a refresh keeps the
 * operator's last view.
 *
 * Touch-sized on mobile/tablet (≥40px hit area).
 */
export function RendererToggle() {
  const renderer = useDashboardStore((s) => s.buildingRenderer);
  const setRenderer = useDashboardStore((s) => s.setBuildingRenderer);
  const view = useDashboardStore((s) => s.view);
  const bp = useBreakpoint();
  if (view.kind !== 'building') return null;

  const isCompact = bp !== 'desktop';
  return (
    <div
      role="radiogroup"
      aria-label="Building renderer"
      style={{
        display: 'inline-flex',
        border: '1px solid #21262d',
        borderRadius: 6,
        overflow: 'hidden',
        fontSize: 11,
      }}
    >
      {(['pixi', 'dom'] as const).map((r) => {
        const active = r === renderer;
        return (
          <button
            key={r}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => setRenderer(r as BuildingRenderer)}
            style={{
              background: active ? '#1f6feb' : 'transparent',
              color: active ? 'white' : '#8b949e',
              border: 'none',
              padding: isCompact ? '8px 14px' : '5px 10px',
              minHeight: isCompact ? 36 : 'auto',
              minWidth: isCompact ? 48 : 'auto',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              fontWeight: active ? 600 : 400,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              touchAction: 'manipulation',
            }}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}
